import { Inject, Injectable, computed, effect, inject, signal } from '@angular/core';
import { Observable, tap } from 'rxjs';
import { AuthService } from '../../../authentication/application/services/auth.service';
import { Notification } from '../entities/notification.entity';
import {
  NOTIFICATION_PROVIDER_PORT,
  NotificationProviderPort,
} from '../ports/notification-provider.port';
import {
  NOTIFICATION_REALTIME_PORT,
  NotificationRealtimePort,
} from '../ports/notification-realtime.port';

const PAGE_SIZE = 20;

@Injectable({ providedIn: 'root' })
export class NotificationService {
  private readonly authService = inject(AuthService);

  private readonly _items = signal<Notification[]>([]);
  private readonly _unreadCount = signal(0);
  private readonly _loading = signal(false);
  private readonly _init = signal(0);
  private readonly _totalPages = signal<number | null>(null);

  readonly items = this._items.asReadonly();
  readonly unreadCount = this._unreadCount.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly hasMore = computed(() => {
    const totalPages = this._totalPages();
    if (totalPages === null) return this._items().length === 0;
    return this._init() / PAGE_SIZE + 1 < totalPages;
  });

  constructor(
    @Inject(NOTIFICATION_PROVIDER_PORT) private readonly provider: NotificationProviderPort,
    @Inject(NOTIFICATION_REALTIME_PORT) private readonly realtime: NotificationRealtimePort,
  ) {
    this.realtime.notification$.subscribe((notification) => {
      this._items.update((items) => [notification, ...items]);
    });

    this.realtime.unreadCount$.subscribe((count) => {
      this._unreadCount.set(count);
    });

    // Conecta/desconecta el canal WebSocket según haya sesión activa.
    effect(() => {
      if (this.authService.session()) {
        this.realtime.connect();
        this.refreshUnreadCount();
      } else {
        this.realtime.disconnect();
        this.reset();
      }
    });
  }

  loadInitial(): void {
    this._init.set(0);
    this._items.set([]);
    this._totalPages.set(null);
    this.fetchPage(0);
  }

  loadMore(): void {
    if (!this.hasMore() || this._loading()) return;
    this.fetchPage(this._init() + PAGE_SIZE);
  }

  refreshUnreadCount(): void {
    this.provider.unreadCount().subscribe((count) => this._unreadCount.set(count));
  }

  markRead(uuid: string): Observable<void> {
    return this.provider.markAsRead(uuid).pipe(
      tap(() => {
        this._items.update((items) =>
          items.map((n) => (n.uuid === uuid ? { ...n, read: true } : n)),
        );
        this.refreshUnreadCount();
      }),
    );
  }

  markAllRead(): Observable<void> {
    return this.provider.markAllAsRead().pipe(
      tap(() => {
        this._items.update((items) => items.map((n) => ({ ...n, read: true })));
        this._unreadCount.set(0);
      }),
    );
  }

  clear(uuid: string): Observable<void> {
    return this.provider.clear(uuid).pipe(
      tap(() => {
        this._items.update((items) => items.filter((n) => n.uuid !== uuid));
        this.refreshUnreadCount();
      }),
    );
  }

  clearAll(): Observable<void> {
    return this.provider.clearAll().pipe(
      tap(() => {
        this._items.set([]);
        this._unreadCount.set(0);
        this._totalPages.set(null);
        this._init.set(0);
      }),
    );
  }

  private fetchPage(init: number): void {
    this._loading.set(true);
    this.provider.list(init, PAGE_SIZE).subscribe({
      next: (page) => {
        this._items.update((items) => (init === 0 ? page.items : [...items, ...page.items]));
        this._init.set(init);
        this._totalPages.set(page.paging.totalPages);
        this._loading.set(false);
      },
      error: () => this._loading.set(false),
    });
  }

  private reset(): void {
    this._items.set([]);
    this._unreadCount.set(0);
    this._init.set(0);
    this._totalPages.set(null);
  }
}
